import { getDefaultConfig } from '@/config/partials/default.config'
import { z } from 'zod'
import { NavConfig } from './partials/navigation.config'

const envSchema = z
    .object({
        NODE_ENV: z.string().optional(),
        NEXT_PUBLIC_SENTRY_ENV: z.string().optional(),
        // NEXT_PUBLIC_API_VERSION: z.string().min(1, 'API version is required'),
        NEXT_PUBLIC_SUPPORT_MAIL: z.string().optional(),
        NEXT_PUBLIC_REACT_QUERY_DEVTOOLS: z.string().optional(),
        NEXT_PUBLIC_SHOW_DEBUG_OVERLAYS: z.string().optional(),
        NEXT_PUBLIC_SENTRY_DSN: z.string().optional(),
        NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE: z.string().optional(),
        NEXT_PUBLIC_SENTRY_DEBUG: z.string().optional(),
        NEXT_PUBLIC_SENTRY_REPLAYS_SESSION_SAMPLE_RATE: z.string().optional(),
        NEXT_PUBLIC_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE: z.string().optional(),
        NEXT_PUBLIC_SENTRY_ENABLED: z.string().optional(),
        NEXT_PUBLIC_ADVANCED_MATCHING: z.string().optional(),
    })
    .superRefine((env, ctx) => {
        const sentryEnabled = env.NEXT_PUBLIC_SENTRY_ENABLED === 'true'

        if (sentryEnabled) {
            if (!env.NEXT_PUBLIC_SENTRY_ENV) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['NEXT_PUBLIC_SENTRY_ENV'],
                    message: 'NEXT_PUBLIC_SENTRY_ENV is required when SENTRY_ENABLED is true',
                })
            }
            if (!env.NEXT_PUBLIC_SENTRY_DSN) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['NEXT_PUBLIC_SENTRY_DSN'],
                    message: 'SENTRY_DSN is required when SENTRY_ENABLED is true',
                })
            }
            if (!env.NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE'],
                    message: 'SENTRY_TRACES_SAMPLE_RATE is required when SENTRY_ENABLED is true',
                })
            }
            if (!env.NEXT_PUBLIC_SENTRY_REPLAYS_SESSION_SAMPLE_RATE) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['NEXT_PUBLIC_SENTRY_REPLAYS_SESSION_SAMPLE_RATE'],
                    message: 'SENTRY_REPLAYS_SESSION_SAMPLE_RATE is required when SENTRY_ENABLED is true',
                })
            }
            if (!env.NEXT_PUBLIC_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['NEXT_PUBLIC_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE'],
                    message: 'SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE is required when SENTRY_ENABLED is true',
                })
            }
        }
    })

// Explicit  mapping of environment variables
// - process.env is not availabe in the browser, so we need to use the rawEnv object to set the values (eg on build time)
const rawEnv = {
    NODE_ENV: process.env.NODE_ENV,
    NEXT_PUBLIC_SENTRY_ENV: process.env.NEXT_PUBLIC_SENTRY_ENV,
    NEXT_PUBLIC_SUPPORT_MAIL: process.env.NEXT_PUBLIC_SUPPORT_MAIL,
    NEXT_PUBLIC_REACT_QUERY_DEVTOOLS: process.env.NEXT_PUBLIC_REACT_QUERY_DEVTOOLS,
    NEXT_PUBLIC_SHOW_DEBUG_OVERLAYS: process.env.NEXT_PUBLIC_SHOW_DEBUG_OVERLAYS,
    NEXT_PUBLIC_SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN || '',
    NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE: process.env.NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE,
    NEXT_PUBLIC_SENTRY_DEBUG: process.env.NEXT_PUBLIC_SENTRY_DEBUG,
    NEXT_PUBLIC_SENTRY_REPLAYS_SESSION_SAMPLE_RATE: process.env.NEXT_PUBLIC_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
    NEXT_PUBLIC_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE: process.env.NEXT_PUBLIC_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
    NEXT_PUBLIC_SENTRY_ENABLED: process.env.NEXT_PUBLIC_SENTRY_ENABLED,
    NEXT_PUBLIC_ADVANCED_MATCHING: process.env.NEXT_PUBLIC_ADVANCED_MATCHING,
}

const parsed = envSchema.safeParse(rawEnv)

if (!parsed.success) {
    console.error('❌ Invalid client environment variables:')
    for (const issue of parsed.error.issues) {
        console.error(`- ${issue.path.join('.') || '[root]'}: ${issue.message}`)
    }
    throw new Error('❌ Invalid client config - check your environment variables')
}

const env = parsed.data
const isProd = env.NODE_ENV === 'production'

const defaultConfig = getDefaultConfig()

export const clientConfig = {
    FaviconVersion: 'rMBK9pOn9j',
    isServer: typeof window === 'undefined',
    isProd,
    showReactQueryDevTools: env.NEXT_PUBLIC_REACT_QUERY_DEVTOOLS === 'true',
    supportMail: env.NEXT_PUBLIC_SUPPORT_MAIL,

    // API  configuration
    apiVersion: 'v1',
    backendServiceGateway: '/api/v1/service',

    placeholderImage: '/placeholder.png',

    pageTitleSuffix: 'Frage Walter Match',

    appSettings: {
        characterName: defaultConfig.characterName,
        title: defaultConfig.title,
        description: defaultConfig.description,
        viewport: 'width=device-width, initial-scale=1, shrink-to-fit=no',
        language: defaultConfig.language,
        navigation: NavConfig.get(),
    },

    legal: {
        terms: { url: '/terms', target: '_blank', title: 'AGB' },
        privacy: { url: '/privacy', target: '_blank', title: 'Datenschutz' },
        imprint: { url: 'https://fragewalter.de/impressum', target: '_blank', title: 'Impressum' },
    },

    releaseNotes: {
        url: '/releases',
        target: '_blank',
        title: 'Release Notes',
    },

    sentry: {
        dsn: env.NEXT_PUBLIC_SENTRY_DSN,
        tracesSampleRate: env.NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE,
        debug: env.NEXT_PUBLIC_SENTRY_DEBUG === 'true',
        replaysSessionSampleRate: env.NEXT_PUBLIC_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
        replaysOnErrorSampleRate: env.NEXT_PUBLIC_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
        environment: env.NEXT_PUBLIC_SENTRY_ENV,
        enabled: env.NEXT_PUBLIC_SENTRY_ENABLED === 'true',
    },
    advancedMatchingEnabled: env.NEXT_PUBLIC_ADVANCED_MATCHING === 'true',
    showDebugOverlays: z
        .boolean()
        .parse(
            process.env.NEXT_PUBLIC_SHOW_DEBUG_OVERLAYS === 'true' &&
                (process.env.NEXT_PUBLIC_APP_ENV === 'development' || process.env.NEXT_PUBLIC_APP_ENV === 'staging'),
        ),
}
